import {
  API_KEYS_ARTICLE,
  BINANCE_API_KEYS_PATH,
  CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID,
} from "@shared/features/onboarding/constants";
import {
  getCreateApiLink,
  getQuickConnectGuideList,
  getSignUpLink,
} from "@shared/features/onboarding/utils";
import React from "react";
import { PRIMARY_EXCHANGES } from "@shared/common/constants";
import Link from "common/Typography/Link";
import IntercomLink from "common/Text/IntercomLink";
import Text from "common/Typography/Text";
import { ExtraInfoType } from "@shared/features/onboarding/types";
import styled from "styled-components";
import useSharedConnectExchangeExtraInfo from "@shared/features/portfolio/createAccount/useSharedConnectExchangeExtraInfo";
import { ConfigurationExchangeInfo, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

interface UseConnectExchangeExtraInfoProps {
  primaryExchange?: string;
  selectedSubExchange?: ConfigurationExchangeInfo;
  isQuickConnect?: boolean;
}

const useConnectExchangeExtraInfo = ({
  primaryExchange,
  selectedSubExchange,
  isQuickConnect,
}: UseConnectExchangeExtraInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isWhitelabel } = useBranding();
  const { createApiKeysGuideList, isDemoWorkspace, exchangesData } =
    useSharedConnectExchangeExtraInfo({
      primaryExchange,
      selectedSubExchange,
    });

  let quickConnectGuideList: (string | React.JSX.Element)[] =
    getQuickConnectGuideList(primaryExchange, t);
  if (
    isQuickConnect &&
    primaryExchange &&
    primaryExchange === PRIMARY_EXCHANGES.BINANCE
  ) {
    quickConnectGuideList = [
      <>
        {t("create_account.binance_terms_prefix")}{" "}
        <Link href={BINANCE_API_KEYS_PATH} target={"_blank"}>
          {t("create_account.binance_terms_link")}
        </Link>
      </>,
      ...quickConnectGuideList,
    ];
  }

  const guideItemsList = isQuickConnect
    ? quickConnectGuideList
    : createApiKeysGuideList;

  const createApiLink = getCreateApiLink(primaryExchange, exchangesData);
  const signUpLink = getSignUpLink(primaryExchange, exchangesData);
  const showSignUpExchangeLink = !isWhitelabel && !!signUpLink;

  const connectApiGuide = (
    <div style={{ width: "100%" }}>
      {guideItemsList?.map((item, index) => (
        <ListItem key={index}>
          {index + 1}. {item}
        </ListItem>
      ))}
      {primaryExchange === PRIMARY_EXCHANGES.HYPERLIQUID && (
        <Text style={{ marginTop: 8, fontStyle: "italic" }}>
          {t("create_account.hyperliquid_note")}
        </Text>
      )}
      {!isQuickConnect && createApiLink && (
        <IntercomLink
          articleLink={createApiLink}
          style={{ display: "inline-block", marginTop: 4 }}
        />
      )}
    </div>
  );

  const signUpExchangeLink = showSignUpExchangeLink ? (
    <Link href={signUpLink} target="_blank">
      {t("create_account.sign_up_prefix")} {primaryExchange}
    </Link>
  ) : null;

  const apiKeysDescription = (
    <>
      <Text style={{ marginBottom: 8 }}>
        {t("create_account.api_keys_description")}
      </Text>
      <IntercomLink articleId={API_KEYS_ARTICLE} />
    </>
  );

  const extraInfoToTitle = {
    [ExtraInfoType.CONNECT_API]: !isQuickConnect
      ? t("create_account.manually_connect_prefix") + " " + primaryExchange
      : t("create_account.quick_connect_prefix") + " " + primaryExchange,
    [ExtraInfoType.NO_EXCHANGE_ACCOUNT]:
      t("create_account.no_account_prefix") +
      " " +
      primaryExchange +
      " " +
      t("create_account.no_account_suffix"),
    [ExtraInfoType.API_KEYS]: t("exchange.api_keys_info"),
  };
  const extraInfoToDescription = {
    [ExtraInfoType.CONNECT_API]: connectApiGuide,
    [ExtraInfoType.NO_EXCHANGE_ACCOUNT]: signUpExchangeLink,
    [ExtraInfoType.API_KEYS]: apiKeysDescription,
  };

  const demoAccountExtraInfo = {
    title: t("create_account.demo_account_extra_info_title"),
    content: (
      <>
        <Text style={{ marginBottom: 8 }}>
          {t("create_account.demo_account_extra_info_content")}
        </Text>
        <IntercomLink articleId={CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID} />
      </>
    ),
  };

  const apiKeysGuide = {
    items: createApiKeysGuideList ?? [],
    note:
      primaryExchange === PRIMARY_EXCHANGES.HYPERLIQUID
        ? t("create_account.hyperliquid_note")
        : undefined,
    createApiLink,
  };

  return {
    isDemoWorkspace,
    extraInfoToTitle,
    extraInfoToDescription,
    demoAccountExtraInfo,
    showSignUpExchangeLink,
    apiKeysGuide,
    signUpExchangeLink,
  };
};

export default useConnectExchangeExtraInfo;

const ListItem = styled(Text)`
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;
